import { useEffect, useState } from "react";
import { message } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomInputField from "../../../../../../CustomHTMLElements/CustomInputField";
import CustomTextArea from "../../../../../../../NewComponents/CustomHTMLElements/CustomTextArea";
import useBusinessTypes from "../../../../hooks/useBusinessTypes";
import useUserPositions from "../../../../hooks/useUserPositions";
import useBusinessSectors from "../../../../../../../custom-hooks/useBusinessSectors";
import useWorldCountries from "../../../../../../../custom-hooks/useWorldCountries";
import useCountryStates from "../../../../../../../custom-hooks/useCountryStates";
import { putData } from "../../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../../api/ajax-endpoints";
import { AllLimitedLiabilitySteps } from "../..";
import ErrorHandler from "../../../../../../ErrorHandler/ErrorHandler.component";
import { ICorporateCustomer } from "../../../../hooks/useCorporateCustomers";

import styles from "../../LimitedLiability.module.scss";
import { errorHandler } from "../../../../../../../helpers/errorHandler";

const schema = yup.object().shape({
  country: yup
    .string()
    .required("Please select country")
    .min(1, "Country is required")
    .max(128),
  city: yup
    .string()
    .required("Please enter your city name")
    .min(1, "City is required")
    .max(128),
  state: yup
    .string()
    .required("Please enter your state name")
    .min(1, "State is required")
    .max(128),
  businessName: yup
    .string()
    .required("Business name is required")
    .min(1)
    .max(128),
  businessRegistrationNumber: yup.string().required("Please enter rc number"),
  taxIdentificationNumber: yup
    .string()
    .required("Please enter tax identification number"),
  address: yup.string().required("Please enter your address").min(1).max(128),
  businessDescription: yup
    .string()
    .required("Please enter business description"),
  industryId: yup.string().required("Please select industry"),
});

interface ILimitedLiabilityBusinessInformation {
  handleProceed: (step: number) => void;
  resumeCustomerData: ICorporateCustomer;
  formData: any;
}

const LimitedLiabilityBusinessInformation: React.FC<
  ILimitedLiabilityBusinessInformation
> = ({ handleProceed, formData, resumeCustomerData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: businessTypesData, isLoading: isLoadingBusinessTypes } =
    useBusinessTypes();
  const { data: userPositionsData, isLoading: isLoadingUserPositions } =
    useUserPositions();
  const { data: businessSectorsData, isLoading: isLoadingBusinessSectors } =
    useBusinessSectors();
  const { data: countriesData, isLoading: isLoadingCountries } =
    useWorldCountries(1); // sourceTypeId 1 for admin/back-office

  // Extract data arrays from response structures
  const businessTypes = businessTypesData?.data || [];
  const userPositions = userPositionsData?.data || [];
  const businessSectors = businessSectorsData?.data || [];
  const countries = countriesData?.data || [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      country: "",
      city: "",
      state: "",
      businessName: formData?.businessName || resumeCustomerData?.biz_name,
      businessRegistrationNumber:
        formData?.businessRegistrationNumber ||
        resumeCustomerData?.biz_registration_number,
      taxIdentificationNumber: "",
      address: "",
      businessDescription: "",
      industryId: formData?.sector_id || resumeCustomerData?.sector_id,
    },
  });

  const watchCountry = watch("country");

  // Resolve country info to get country ID for state loading
  const resolveCountryInfo = countries?.find(
    ({ iso2 }: { iso2: string }) => iso2 === watchCountry
  );

  const { data: statesData, isLoading: isLoadingStates } = useCountryStates(
    resolveCountryInfo?.id || 0
  );
  const states = statesData?.data || [];

  useEffect(() => {
    if (watchCountry && watchCountry !== "") {
      // Reset state when country changes
      setValue("state", "");
    }
  }, [watchCountry, setValue]);

  const handleSave = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload = {
        address: data.address,
        businessDescription: data.businessDescription,
        businessName: data.businessName,
        businessRegistrationNumber: data.businessRegistrationNumber,
        businessType: formData?.customerSubType,
        city: data.city,
        industryId: Number(data.industryId),
        state: data.state,
        taxIdentificationNumber: data.taxIdentificationNumber,
        userPosition: formData?.userPosition || resumeCustomerData?.position_id,
        countryCode: data.country,
        customerId: formData?.funder_id || resumeCustomerData?.customer_id,
      };

      await putData(ajaxEndpoints.UPDATE_BUSINESS_INFORMATION, payload);
      message.success("Business information saved successfully!");
      // Navigate to next step
      handleProceed(AllLimitedLiabilitySteps.DIRECTORS);
    } catch (error: any) {
      message.error(
        errorHandler(error) || "Failed to save business information"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    handleProceed(AllLimitedLiabilitySteps.DIRECTORS);
  };

  const handleSaveClick = () => {
    handleSubmit(handleSave)();
  };

  useEffect(() => {
    setValue(
      "industryId",
      formData?.sector_id || resumeCustomerData?.sector_id
    );
    setValue(
      "businessDescription",
      formData?.biz_description || resumeCustomerData?.biz_description
    );
    setValue("country", "NG");
    setValue("state", formData?.state_name || resumeCustomerData?.state_name);
    setValue("city", formData?.city || resumeCustomerData?.city);
    setValue(
      "address",
      formData?.biz_Address || resumeCustomerData?.biz_Address
    );
  }, []);

  return (
    <div>
      <div className="mt-5">
        <form
          onSubmit={handleSubmit(handleSave)}
          className={styles.businessForm}
        >
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label>
                  Business Type <span className="text-danger">*</span>{" "}
                  {isLoadingBusinessTypes && <small>(Loading...)</small>}
                </label>
                <select
                  name="customerSubType"
                  className="form-control"
                  disabled
                  value={formData?.customerSubType || ""}
                >
                  <option value="">Select type</option>
                  {businessTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Business Name *"
                name="businessName"
                placeholder="e.g., ABC Limited"
                reference={register}
                errors={errors.businessName}
                readOnly
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Industry <span className="text-danger">*</span>{" "}
                  {isLoadingBusinessSectors && <small>(Loading...)</small>}
                </label>
                <select
                  name="industryId"
                  className="form-control"
                  ref={register}
                >
                  <option value="">Select industry</option>
                  {businessSectors.map((sector: any) => (
                    <option key={sector.category_id} value={sector.category_id}>
                      {sector.category_name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.industryId} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="form-group">
                <label>
                  Position <span className="text-danger">*</span>{" "}
                  {isLoadingUserPositions && <small>(Loading...)</small>}
                </label>
                <select
                  name="userPosition"
                  className="form-control"
                  disabled
                  value={
                    formData?.userPosition || resumeCustomerData?.position_id
                  }
                >
                  <option value="">Select position</option>
                  {userPositions.map((position: any) => (
                    <option key={position.id} value={position.id}>
                      {position.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomTextArea
                label="How does your business create value? *"
                name="businessDescription"
                placeholder="Describe your business..."
                reference={register}
                errors={errors.businessDescription}
                rows={5}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Tax Identification Number *"
                name="taxIdentificationNumber"
                placeholder="Enter tax identification number"
                reference={register}
                errors={errors.taxIdentificationNumber}
                maxLength={128}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Business Registration Number *"
                name="businessRegistrationNumber"
                placeholder="Enter business registration number"
                reference={register}
                errors={errors.businessRegistrationNumber}
                readOnly
                maxLength={128}
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Country <span className="text-danger">*</span>{" "}
                  {isLoadingCountries && <small>(Loading...)</small>}
                </label>
                <select name="country" className="form-control" ref={register}>
                  <option value="">Select country</option>
                  {countries.map((country: any) => (
                    <option key={country.id} value={country.iso2}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.country} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="form-group">
                <label>
                  State <span className="text-danger">*</span>{" "}
                  {isLoadingStates && <small>(Loading...)</small>}
                </label>
                <select
                  name="state"
                  className="form-control"
                  ref={register}
                  disabled={!watchCountry}
                >
                  <option value="">Select state</option>
                  {states.map((state: any) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.state} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Business City *"
                name="city"
                placeholder="Enter city"
                reference={register}
                errors={errors.city}
                maxLength={128}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Business Street Address *"
                name="address"
                placeholder="Enter your street address"
                reference={register}
                errors={errors.address}
                maxLength={128}
              />
            </div>
          </div>

          <div className={styles.ActionButtonContainer}>
            <button
              type="button"
              onClick={handleSaveClick}
              className="btn advancly-btn btn-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="btn advancly-btn btn-lg"
            >
              Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LimitedLiabilityBusinessInformation;
